import { Col, Row } from 'antd';
import React from 'react';
import {
  Gutter,
  Title,
  Text,
  LoadingState,
  AlignedInfo,
  ErrorHandler,
} from '@common/components';
import type { OrganizationUnitType } from '@institutions/graphql';
import { tierHumanizeVisibilityOffset } from '@institutions/services';
import { RolesQuery } from '@clinician-profile/graphql';
import { useQuery } from '@common/hooks';
import type { TieringRadioTierType } from '@institutions/components';
import { SupplierDetailsSummaryUnits } from './_supplier-details-summary-units';

export function SupplierDetailsSummary({
  selectedOrganizationUnits,
  removedOrganizationUnits,
  selectedRoles,
  selectedTier,
  canUserSeeTiering = true,
  extraColumn,
}: {
  selectedOrganizationUnits: OrganizationUnitType[];
  removedOrganizationUnits?: OrganizationUnitType[];
  selectedRoles: string[];
  selectedTier: TieringRadioTierType;
  canUserSeeTiering: boolean;
  extraColumn?: React.ReactNode;
}) {
  const {
    loading: rolesLoading,
    error: rolesError,
    data: rolesData,
  } = useQuery(RolesQuery, { variables: { ids: selectedRoles } });

  const humanizedVisibilityOffset = tierHumanizeVisibilityOffset(
    selectedTier.visibilityOffset
  );

  return (
    <Row gutter={[40, 40]}>
      <SupplierDetailsSummaryUnits
        title="Permitted Units"
        units={selectedOrganizationUnits}
      />

      <SupplierDetailsSummaryUnits
        title="Removed Units"
        units={removedOrganizationUnits}
      />

      {selectedRoles.length > 0 && (
        <Col>
          <Gutter size="lg" direction="bottom">
            <Gutter size="md" direction="bottom">
              <Title size="h3" weight="medium">
                Roles Access ({selectedRoles.length})
              </Title>
            </Gutter>
            {rolesLoading && <LoadingState />}
            {rolesError && (
              <ErrorHandler
                error={rolesError}
                log="RolesQueryErrorOnSupplierSummaryStep"
              />
            )}
            {rolesData?.roles?.length && (
              <ul>
                {rolesData.roles.map((role) => (
                  <li key={role.id}>{role.name}</li>
                ))}
              </ul>
            )}
          </Gutter>
        </Col>
      )}

      {canUserSeeTiering && selectedTier.name && (
        <Col>
          <div>
            <Gutter size="md" direction="bottom">
              <Title size="h3" weight="medium">
                Tier
              </Title>
            </Gutter>

            <Gutter size="sm" direction="bottom">
              <Text bold size="lg" block>
                {selectedTier.name}
              </Text>
            </Gutter>

            <AlignedInfo
              content={[
                ['Staffing Request Delay', humanizedVisibilityOffset],
                ['Admin Fee', `${selectedTier.fee}%`],
              ]}
            />
          </div>
        </Col>
      )}

      {extraColumn && <Col>{extraColumn}</Col>}
    </Row>
  );
}
